import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const hours = Array.from({ length: 24 }, (_, i) => i);

const SchedulerContainer = styled(Paper)(({ theme }) => ({
    padding: '8px',
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'auto',
    maxHeight: '280px',
    '&::-webkit-scrollbar': {
        width: '5px',
        height: '5px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        borderRadius: '4px',
        backgroundColor: 'rgba(99, 87, 129, 0.3)',
        '&:hover': {
            backgroundColor: 'rgba(99, 87, 129, 0.5)',
        },
    },
}));

const GridContainer = styled(Box)({
    display: 'inline-block',
    minWidth: '100%',
});

const GridTable = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '35px repeat(24, 22px)',
    gap: '1.5px',
    marginTop: '4px',
});

const TimeHeader = styled(Box)(({ theme }) => ({
    fontSize: '8px',
    fontWeight: '600',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: '2px 1px',
    backgroundColor: 'transparent',
}));

const DayLabel = styled(Box)(({ theme }) => ({
    fontSize: '10px',
    fontWeight: '600',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '4px',
    backgroundColor: 'transparent',
}));

const TimeCell = styled(Box)(({ selected, theme }) => ({
    height: '16px',
    borderRadius: '2px',
    backgroundColor: selected ? '#63578160' : '#f5f5f5',
    border: `1px solid ${selected ? '#483F5E60' : '#e0e0e0'}`,
    cursor: 'default',
}));

const LegendContainer = styled(Box)({
    display: 'flex',
    gap: '12px',
    marginBottom: '8px',
    flexWrap: 'wrap',
});

const LegendItem = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
});

const LegendBox = styled(Box)(({ color }) => ({
    width: '14px',
    height: '14px',
    borderRadius: '3px',
    backgroundColor: color,
    border: '1px solid #e0e0e0',
}));

const ReadOnlySchedulerTable = ({ value, label }) => {
    const [schedule, setSchedule] = useState({});

    // Initialize schedule from value
    useEffect(() => {
        try {
            if (!value) {
                setSchedule({});
                return;
            }

            let parsedValue = value;
            
            // Parse if it's a JSON string
            if (typeof value === 'string') {
                parsedValue = JSON.parse(value);
            }

            // If it's an array (168 elements), convert to object format
            if (Array.isArray(parsedValue)) {
                const scheduleObj = {};
                parsedValue.forEach((val, index) => {
                    if (val > 0) {
                        const day = Math.floor(index / 24);
                        const hour = index % 24;
                        if (day >= 0 && day < 7 && hour >= 0 && hour < 24) {
                            scheduleObj[`${day}-${hour}`] = true;
                        }
                    }
                });
                setSchedule(scheduleObj);
            } else if (typeof parsedValue === 'object') {
                // It's already in object format
                setSchedule(parsedValue);
            }
        } catch (e) {
            console.error('Error parsing schedule value:', e);
            setSchedule({});
        }
    }, [value]);

    const getCellKey = (day, hour) => `${day}-${hour}`;

    const isSelected = (day, hour) => {
        return schedule && schedule[getCellKey(day, hour)] === true;
    };

    const selectedCount = Object.keys(schedule || {}).length;
    const totalCells = 7 * 24;

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography 
                variant="subtitle2" 
                sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.8rem'
                }}
            >
                <span>{label || 'Weekly Schedule'}</span>
                <Typography 
                    variant="caption" 
                    sx={{ 
                        color: 'text.secondary',
                        backgroundColor: '#f5f5f5',
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontWeight: 500,
                        fontSize: '0.65rem'
                    }}
                >
                    {selectedCount} / {totalCells} hours
                </Typography>
            </Typography>

            <LegendContainer>
                <LegendItem>
                    <LegendBox color="#635781" />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.65rem' }}>
                        Active
                    </Typography>
                </LegendItem>
                <LegendItem>
                    <LegendBox color="#f5f5f5" />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.65rem' }}>
                        Inactive
                    </Typography>
                </LegendItem>
            </LegendContainer>

            <SchedulerContainer elevation={1}>
                <GridContainer sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex', padding: '4px' }}>
                    <GridTable>
                        {/* Empty corner cell */}
                        <Box />
                        
                        {/* Hour headers */}
                        {hours.map(hour => (
                            <TimeHeader key={hour}>
                                {hour}
                            </TimeHeader>
                        ))}
                        
                        {/* Day rows */}
                        {days.map((day, dayIndex) => (
                            <React.Fragment key={day}>
                                <DayLabel>{day}</DayLabel>
                                {hours.map(hour => (
                                    <TimeCell
                                        key={`${day}-${hour}`}
                                        selected={isSelected(dayIndex, hour)}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </GridTable>
                </GridContainer>
            </SchedulerContainer>
        </Box>
    );
};

export default ReadOnlySchedulerTable;

